// Client+server safe helpers for CMS data. No Payload/Next-server imports
// here — importing from this file is guaranteed not to pull server-only
// modules (db adapters, sharp, etc.) into a client bundle.

import { optimizeCloudinaryUrl } from '@/lib/videoConfig'

export function titleToSlug(title: string): string {
  return String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const ABSOLUTE_URL_OR_PROTOCOL = /^(https?:\/\/|mailto:|tel:|sms:)/i

/**
 * Normalize a CMS-stored URL field for use as an href / lookup key.
 *
 * Editors might enter `about`, `/about`, `//about`, or `/about/` — all of
 * those should resolve to the same canonical `/about`. Absolute URLs
 * (http(s)://, mailto:, tel:, sms:) and anchor-only fragments (`#foo`) are
 * left alone so the frontend can handle them.
 */
export function toCmsHref(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  const v = raw.trim()
  if (!v) return ''
  if (v.startsWith('#')) return v
  if (ABSOLUTE_URL_OR_PROTOCOL.test(v)) return v
  // Path: collapse leading slashes to one, strip trailing slashes (but keep
  // the bare root `/`).
  const withLead = '/' + v.replace(/^\/+/, '')
  if (withLead === '/') return '/'
  return withLead.replace(/\/+$/, '')
}

/**
 * Normalize a CMS-stored slug. Trims whitespace and strips any leading or
 * trailing slashes so `/articles/${slug}` never becomes `/articles//foo`.
 * Internal duplicate slashes are collapsed defensively.
 */
export function toCmsSlug(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  return raw
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .replace(/\/{2,}/g, '/')
}

/**
 * A flat CMS media node after response-shape normalization. Only the fields the
 * UI reads are modeled; everything is optional because mixed payloads during the
 * Payload transition don't always carry every field.
 */
export type CmsMediaNode = {
  url?: string
  alternativeText?: string
  width?: number | string
  height?: number | string
} & Record<string, unknown>

/**
 * Unwrap the common CMS media shapes to a single flat node (the one carrying
 * `url`/`alt`/dimensions), or null when none is present. Handles:
 *   - `{ data: { attributes } }`  (legacy wrapped)
 *   - `{ data: [...] }`           (array under `data`, first element)
 *   - `{ data }`                  (single node under `data`)
 *   - `[...]`                     (bare array, first element)
 *   - `{ url, ... }`              (already-flat node)
 */
export function toMediaNode(media: unknown): CmsMediaNode | null {
  if (!media || typeof media !== 'object') return null

  if (Array.isArray(media)) {
    return media.length ? toMediaNode(media[0]) : null
  }

  const m = media as Record<string, unknown>

  // Already-flat node.
  if (typeof m.url === 'string') return m as CmsMediaNode

  if ('data' in m) {
    const data = m.data
    if (Array.isArray(data)) {
      const first = data[0] as { attributes?: unknown } | undefined
      if (first && typeof first === 'object' && 'attributes' in first) {
        return toMediaNode(first.attributes)
      }
      return toMediaNode(first)
    }
    if (data && typeof data === 'object') {
      const attributes = (data as { attributes?: unknown }).attributes
      if (attributes !== undefined) return toMediaNode(attributes)
      return toMediaNode(data)
    }
  }

  return null
}

/**
 * Normalize a CMS multi-media field into a flat array of media nodes. Mirrors
 * `toMediaNode`'s unwrapping but keeps every element (for galleries and other
 * multi-image fields).
 */
export function toMediaArray(media: unknown): CmsMediaNode[] {
  if (!media) return []

  const unwrap = (entry: unknown): CmsMediaNode | null => {
    if (!entry || typeof entry !== 'object') return null
    const e = entry as { attributes?: unknown }
    if (e.attributes !== undefined) return toMediaNode(e.attributes)
    return toMediaNode(entry)
  }

  if (Array.isArray(media)) {
    return media.map(unwrap).filter((n): n is CmsMediaNode => n !== null)
  }

  const withData = media as { data?: unknown }
  if (Array.isArray(withData.data)) {
    return withData.data
      .map(unwrap)
      .filter((n): n is CmsMediaNode => n !== null)
  }
  if (withData.data) {
    const node = unwrap(withData.data)
    return node ? [node] : []
  }

  const single = toMediaNode(media)
  return single ? [single] : []
}

// Extract a media URL from the common shapes our CMS layer emits. After the
// Payload migration most media objects are flat (`{ url, alt, sizes }`), but
// we keep the legacy response shapes so mixed payloads during transition don't
// break the UI.
export function getCmsMediaUrl(media: unknown): string | null {
  if (!media) return null
  if (typeof media === 'string') return media
  if (typeof media !== 'object') return null

  const m = media as Record<string, unknown>
  if (typeof m.url === 'string') return m.url

  const urlFromData = (m as { data?: { attributes?: { url?: unknown } } })?.data?.attributes?.url
  if (typeof urlFromData === 'string') return urlFromData

  const arrData = (m as { data?: unknown }).data
  if (Array.isArray(arrData)) {
    const first = (arrData as Array<{ attributes?: { url?: unknown } }>)[0]
    const nested = first?.attributes?.url
    if (typeof nested === 'string') return nested
  }

  return null
}

export function getCmsImageUrl(media: unknown): string | null {
  const raw = getCmsMediaUrl(media)
  if (!raw) return null
  return optimizeCloudinaryUrl(raw, false)
}
